/**
 * Time condition node definition.
 * Converts time condition (timer, daily, once) to scene JSON.
 */

import type { NodeDefinition, SceneJsonContext } from '../types';
import type { FlowNode } from '../../../types/flow-types';

export const timeConditionDefinition: NodeDefinition = {
	type: 'timeCondition',
	category: 'condition',
	sourceHandles: ['true'],

	defaultData: () => ({
		label: 'Time Condition',
		timeSubType: 'daily',
		weekdays: [1, 2, 3, 4, 5],
		time: '08:00',
		interval: 30,
		date: new Date().toISOString().split('T')[0],
		executeTime: '12:00',
	}),

	toSceneJson: (node: FlowNode) => {
		if (node.data.sceneData) {
			const sceneData = { ...(node.data.sceneData as any) };
			sceneData._fid = sceneData._fid || node.id;
			return sceneData;
		}
		if (node.data.originalCondition) {
			const original = { ...(node.data.originalCondition as any) };
			original._fid = original._fid || node.id;
			return original;
		}

		const subType = (node.data.timeSubType as string) || 'daily';

		// Timer: every N minutes
		if (subType === 'timer') {
			return {
				dminslimit: String(node.data.interval ?? 30),
				rrtrig: { rr: 'dt:t' },
				_gui_type: 'timer',
				_fid: node.id,
			};
		}

		// Execute once: specific date and time
		if (subType === 'ot_cond' || subType === 'execute_once') {
			const date = (node.data.date as string) || new Date().toISOString().split('T')[0];
			const time = (node.data.executeTime as string) || '00:00';
			return {
				eq: [{ rr: 'dt:dt' }, `${date} ${time}`],
				_gui_type: 'ot_cond',
				_fid: node.id,
			};
		}

		// Daily/Weekly: time and optional weekdays
		const time = (node.data.time as string) || '08:00';
		const isin = (node.data.weekdays as number[]) ?? [0, 1, 2, 3, 4, 5, 6];
		const isinStr = isin
			.map((d) => String(d + 1))
			.join(' ')
			.trim();

		if (isinStr && isin.length > 0) {
			return {
				and: [{ eq: [{ rr: 'dt:t' }, time] }, { wday: 'now', isin: isinStr }],
				_gui_type: 'd_cond',
				_fid: node.id,
			};
		}

		return {
			eq: [{ rr: 'dt:t' }, time],
			_gui_type: 'd_cond',
			_fid: node.id,
		};
	},
};
